/* ============================================================
   AuthModal — Iniciar sesion / Crear cuenta (Cuponeo)
   ============================================================ */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useRecaptcha from '../hooks/useRecaptcha';
import { notifyAuthChange } from '../hooks/useAuth';
import './AuthModal.css';

const logoColor = '/logo-color.svg';

export default function AuthModal({ isOpen, onClose, onAuth }) {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [cedula, setCedula] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [departamentoId, setDepartamentoId] = useState('');
  const [ciudadId, setCiudadId] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { execute: execRecaptcha } = useRecaptcha();

  const { data: ubicaciones } = useQuery({
    queryKey: ['ubicaciones'],
    queryFn: async () => {
      const res = await fetch('/api/ubicaciones').then(r => r.json());
      return res.datos || [];
    },
    enabled: isOpen,
    staleTime: 600000,
  });

  const ciudadesDelDepto = useMemo(() => {
    const dep = (ubicaciones || []).find(d => String(d.id) === String(departamentoId));
    return dep?.ciudades || [];
  }, [ubicaciones, departamentoId]);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const recaptchaToken = await execRecaptcha('login');
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, recaptcha_token: recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.exito) throw new Error(data.mensaje || 'Credenciales incorrectas');
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.usuario));
      notifyAuthChange();
      onAuth?.(data.usuario);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Las contrasenas no coinciden'); return; }
    setLoading(true);
    try {
      const recaptchaToken = await execRecaptcha('register');
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, password,
          cedula: cedula || null,
          telefono: telefono || null,
          direccion: direccion || null,
          departamento_id: departamentoId || null,
          ciudad_id: ciudadId || null,
          recaptcha_token: recaptchaToken,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.exito) {
        const msg = data.errors
          ? Object.values(data.errors).flat().join('. ')
          : data.mensaje || data.message || 'Error al registrar';
        throw new Error(msg);
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.usuario));
      notifyAuthChange();
      onAuth?.(data.usuario);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail(''); setPassword(''); setName(''); setConfirmPw('');
    setCedula(''); setTelefono(''); setDireccion(''); setDepartamentoId(''); setCiudadId('');
    setError(''); setShowPw(false);
  };

  const switchTab = (t) => { setTab(t); setError(''); };

  return (
    <div className="auth-overlay" onClick={(e) => { if (e.target === e.currentTarget) { onClose(); resetForm(); } }}>
      <div className="auth-modal">
        <button className="auth-modal__close" onClick={() => { onClose(); resetForm(); }} aria-label="Cerrar">×</button>

        <div className="auth-modal__logo">
          <img src={logoColor} alt="Cuponeo" />
        </div>

        <div className="auth-modal__header">
          <h2>Bienvenido a Cuponeo</h2>
          <p>Los mejores cupones de descuento de Colombia</p>
        </div>

        <div className="auth-tabs">
          <button className={`auth-tabs__btn ${tab === 'login' ? 'auth-tabs__btn--active' : ''}`} onClick={() => switchTab('login')}>Iniciar Sesion</button>
          <button className={`auth-tabs__btn ${tab === 'register' ? 'auth-tabs__btn--active' : ''}`} onClick={() => switchTab('register')}>Crear Cuenta</button>
        </div>

        {tab === 'login' && (
          <form className="auth-form" onSubmit={handleLogin}>
            {error && <div className="auth-error">{error}</div>}
            <div className="auth-field">
              <label>Correo electronico</label>
              <div className="auth-field__wrap">
                <input type="email" placeholder="ejemplo@correo.co" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
              </div>
            </div>
            <div className="auth-field">
              <label>Contrasena</label>
              <div className="auth-field__wrap">
                <input type={showPw ? 'text' : 'password'} placeholder="Tu contrasena" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" className="auth-field__toggle" onClick={() => setShowPw(!showPw)}>{showPw ? 'Ocultar' : 'Ver'}</button>
              </div>
            </div>
            <div className="auth-forgot"><a href="/recuperar">¿Olvidaste tu contrasena?</a></div>
            <button type="submit" className="auth-btn" disabled={loading}>{loading ? 'Ingresando...' : 'Entrar'}</button>
          </form>
        )}

        {tab === 'register' && (
          <form className="auth-form" onSubmit={handleRegister}>
            {error && <div className="auth-error">{error}</div>}
            <div className="auth-field">
              <label>Nombre completo</label>
              <div className="auth-field__wrap">
                <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" />
              </div>
            </div>
            <div className="auth-field">
              <label>Correo electronico</label>
              <div className="auth-field__wrap">
                <input type="email" placeholder="ejemplo@correo.co" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
              </div>
            </div>
            <div className="auth-field">
              <label>Cedula</label>
              <div className="auth-field__wrap">
                <input type="text" placeholder="Numero de cedula" value={cedula} onChange={e => setCedula(e.target.value)} />
              </div>
            </div>
            <div className="auth-field">
              <label>Telefono</label>
              <div className="auth-field__wrap">
                <input type="tel" placeholder="Numero de telefono" value={telefono} onChange={e => setTelefono(e.target.value)} />
              </div>
            </div>
            <div className="auth-field">
              <label>Direccion</label>
              <div className="auth-field__wrap">
                <input type="text" placeholder="Tu direccion" value={direccion} onChange={e => setDireccion(e.target.value)} />
              </div>
            </div>
            <div className="auth-field">
              <label>Departamento</label>
              <div className="auth-field__wrap">
                <select value={departamentoId} onChange={e => { setDepartamentoId(e.target.value); setCiudadId(''); }} style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none' }}>
                  <option value="">Selecciona departamento</option>
                  {(ubicaciones || []).map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
              </div>
            </div>
            <div className="auth-field">
              <label>Ciudad</label>
              <div className="auth-field__wrap">
                <select value={ciudadId} onChange={e => setCiudadId(e.target.value)} disabled={!departamentoId} style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none' }}>
                  <option value="">Selecciona ciudad</option>
                  {ciudadesDelDepto.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            </div>
            <div className="auth-field">
              <label>Contrasena</label>
              <div className="auth-field__wrap">
                <input type={showPw ? 'text' : 'password'} placeholder="Minimo 6 caracteres" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} autoComplete="new-password" />
                <button type="button" className="auth-field__toggle" onClick={() => setShowPw(!showPw)}>{showPw ? 'Ocultar' : 'Ver'}</button>
              </div>
            </div>
            <div className="auth-field">
              <label>Confirmar contrasena</label>
              <div className="auth-field__wrap">
                <input type="password" placeholder="Repite tu contrasena" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required minLength={6} autoComplete="new-password" />
              </div>
            </div>
            <button type="submit" className="auth-btn" disabled={loading}>{loading ? 'Creando cuenta...' : 'Crear Cuenta'}</button>
          </form>
        )}

        <div className="auth-divider">
          <div className="auth-divider__line" />
          <span className="auth-divider__text">o continua con</span>
          <div className="auth-divider__line" />
        </div>

        <div className="auth-social">
          <button type="button" className="auth-social__btn" onClick={() => {
            fetch('/api/auth/google/redirect').then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; });
          }}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Google
          </button>
          <button type="button" className="auth-social__btn" onClick={() => {
            fetch('/api/auth/facebook/redirect').then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; });
          }}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#1877F2" d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24c0 11.98 8.776 21.908 20.25 23.708V30.938h-6.094V24h6.094v-5.288c0-6.014 3.583-9.337 9.065-9.337 2.625 0 5.372.469 5.372.469v5.906h-3.026c-2.981 0-3.911 1.85-3.911 3.75V24h6.656l-1.063 6.938H27.75v16.77C39.224 45.908 48 35.98 48 24z"/></svg>
            Facebook
          </button>
        </div>
      </div>
    </div>
  );
}
